import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Plus,
  Minus,
  Trash2,
  CheckCircle,
  DollarSign,
} from "lucide-react";
import { toast } from "sonner";
import { adminFetch, ADMIN_BACKEND_URL } from "./AdminLayout";

export default function AdminManualOrderPage() {
  const [products, setProducts] = useState([]);
  const [items, setItems] = useState([]);
  const [customer, setCustomer] = useState({
    customer_name: "",
    company_name: "",
    email: "",
    phone: "",
    shipping_address: "",
    shipping_city: "",
    shipping_state: "",
    shipping_zip: "",
    notes: "",
  });
  const [paymentType, setPaymentType] = useState("manual");
  const [discountAmount, setDiscountAmount] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [createdOrder, setCreatedOrder] = useState(null);

  useEffect(() => {
    loadProducts();
  }, []);

  const loadProducts = async () => {
    try {
      const res = await adminFetch(`${ADMIN_BACKEND_URL}/api/admin/products`);
      const data = await res.json();
      setProducts(data);
    } catch {
      toast.error("Failed to load products");
    }
  };

  const addItem = (product) => {
    const exists = items.find((i) => i.product_id === product.id);
    if (exists) {
      setItems(
        items.map((i) =>
          i.product_id === product.id ? { ...i, quantity: i.quantity + 1 } : i
        )
      );
    } else {
      setItems([
        ...items,
        {
          product_id: product.id,
          product_name: product.name,
          quantity: 1,
          unit_price: product.price,
        },
      ]);
    }
  };

  const updateItem = (idx, field, value) => {
    const updated = [...items];
    updated[idx] = { ...updated[idx], [field]: field === "quantity" ? parseInt(value) || 1 : parseFloat(value) || 0 };
    setItems(updated);
  };

  const removeItem = (idx) => {
    setItems(items.filter((_, i) => i !== idx));
  };

  const subtotal = items.reduce((sum, i) => sum + i.unit_price * i.quantity, 0);
  const total = Math.max(0, subtotal - discountAmount);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (items.length === 0) {
      toast.error("Add at least one item");
      return;
    }
    if (!customer.customer_name || !customer.company_name || !customer.email) {
      toast.error("Fill in customer name, company, and email");
      return;
    }
    setSubmitting(true);
    try {
      const res = await adminFetch(`${ADMIN_BACKEND_URL}/api/admin/orders/manual`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          items,
          ...customer,
          payment_type: paymentType,
          discount_amount: discountAmount,
        }),
      });
      if (!res.ok) throw new Error("Failed");
      const data = await res.json();
      setCreatedOrder(data);
      toast.success(`Order ${data.order_id} created`);
    } catch {
      toast.error("Failed to create order");
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setItems([]);
    setCustomer({
      customer_name: "",
      company_name: "",
      email: "",
      phone: "",
      shipping_address: "",
      shipping_city: "",
      shipping_state: "",
      shipping_zip: "",
      notes: "",
    });
    setPaymentType("manual");
    setDiscountAmount(0);
    setCreatedOrder(null);
  };

  if (createdOrder) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-16 text-center">
        <div className="bg-white rounded-xl border border-slate-200 p-8">
          <CheckCircle className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: "Manrope, sans-serif" }}>
            Order Created
          </h2>
          <p className="text-lg font-mono text-sky-600 mb-2" data-testid="created-order-id">
            {createdOrder.order_id}
          </p>
          <p className="text-slate-600 mb-1">
            Total: <span className="font-bold text-slate-900">${createdOrder.total?.toFixed(2)}</span>
          </p>
          <p className="text-slate-600 mb-6">
            Payment: <span className="font-medium capitalize">{createdOrder.payment_status}</span>
          </p>
          <Button onClick={resetForm} className="bg-sky-600 hover:bg-sky-700 text-white" data-testid="create-another-order-btn">
            Create Another Order
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-6 md:px-12 py-8">
      <h1 className="text-2xl font-bold text-slate-900 mb-1" style={{ fontFamily: "Manrope, sans-serif" }} data-testid="manual-order-headline">
        Create Manual Order
      </h1>
      <p className="text-slate-500 text-sm mb-8">
        Enter order details for phone, negotiated, or invoice orders.
      </p>

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left: Items */}
          <div className="lg:col-span-2 space-y-6">
            {/* Product picker */}
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h3 className="font-semibold text-slate-900 mb-4">Add Products</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {products.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => addItem(p)}
                    className="text-left p-3 border border-slate-200 rounded-lg hover:border-sky-400 hover:bg-sky-50 transition-colors"
                    data-testid={`add-product-${p.id}`}
                  >
                    <p className="text-sm font-medium text-slate-900 truncate">{p.name}</p>
                    <p className="text-xs text-slate-500">{p.item_code || p.sku} &middot; ${p.price.toFixed(2)} per {p.packaging_unit || "unit"}</p>
                  </button>
                ))}
              </div>
            </div>

            {/* Order items */}
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h3 className="font-semibold text-slate-900 mb-4">Order Items</h3>
              {items.length === 0 ? (
                <p className="text-sm text-slate-400 py-4 text-center">No items added yet</p>
              ) : (
                <div className="space-y-3">
                  {items.map((item, idx) => (
                    <div key={idx} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-900 truncate">{item.product_name}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Label className="text-xs text-slate-500 whitespace-nowrap">Qty</Label>
                        <Input
                          type="number"
                          value={item.quantity}
                          onChange={(e) => updateItem(idx, "quantity", e.target.value)}
                          className="w-20 h-8 text-sm"
                          min="1"
                          data-testid={`item-qty-${idx}`}
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <Label className="text-xs text-slate-500 whitespace-nowrap">$/unit</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={item.unit_price}
                          onChange={(e) => updateItem(idx, "unit_price", e.target.value)}
                          className="w-24 h-8 text-sm"
                          min="0"
                          data-testid={`item-price-${idx}`}
                        />
                      </div>
                      <span className="text-sm font-medium text-slate-700 w-20 text-right">
                        ${(item.unit_price * item.quantity).toFixed(2)}
                      </span>
                      <button
                        type="button"
                        onClick={() => removeItem(idx)}
                        className="text-red-400 hover:text-red-600"
                        data-testid={`remove-item-${idx}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Customer info */}
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h3 className="font-semibold text-slate-900 mb-4">Customer Information</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm">Customer Name *</Label>
                  <Input value={customer.customer_name} onChange={(e) => setCustomer({ ...customer, customer_name: e.target.value })} data-testid="manual-customer-name" />
                </div>
                <div>
                  <Label className="text-sm">Company Name *</Label>
                  <Input value={customer.company_name} onChange={(e) => setCustomer({ ...customer, company_name: e.target.value })} data-testid="manual-company-name" />
                </div>
                <div>
                  <Label className="text-sm">Email *</Label>
                  <Input type="email" value={customer.email} onChange={(e) => setCustomer({ ...customer, email: e.target.value })} data-testid="manual-email" />
                </div>
                <div>
                  <Label className="text-sm">Phone</Label>
                  <Input value={customer.phone} onChange={(e) => setCustomer({ ...customer, phone: e.target.value })} data-testid="manual-phone" />
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-sm">Shipping Address</Label>
                  <Input value={customer.shipping_address} onChange={(e) => setCustomer({ ...customer, shipping_address: e.target.value })} data-testid="manual-address" />
                </div>
                <div>
                  <Label className="text-sm">City</Label>
                  <Input value={customer.shipping_city} onChange={(e) => setCustomer({ ...customer, shipping_city: e.target.value })} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-sm">State</Label>
                    <Input value={customer.shipping_state} onChange={(e) => setCustomer({ ...customer, shipping_state: e.target.value })} />
                  </div>
                  <div>
                    <Label className="text-sm">ZIP</Label>
                    <Input value={customer.shipping_zip} onChange={(e) => setCustomer({ ...customer, shipping_zip: e.target.value })} />
                  </div>
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-sm">Order Notes</Label>
                  <Textarea value={customer.notes} onChange={(e) => setCustomer({ ...customer, notes: e.target.value })} rows={2} data-testid="manual-notes" />
                </div>
              </div>
            </div>
          </div>

          {/* Right: Summary */}
          <div className="space-y-6">
            <div className="bg-white rounded-xl border border-slate-200 p-6 sticky top-4">
              <h3 className="font-semibold text-slate-900 mb-4">Order Summary</h3>

              <div className="space-y-2 text-sm mb-4">
                <div className="flex justify-between text-slate-600">
                  <span>Subtotal ({items.length} items)</span>
                  <span>${subtotal.toFixed(2)}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Label className="text-sm text-slate-600 whitespace-nowrap">Discount $</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={discountAmount}
                    onChange={(e) => setDiscountAmount(parseFloat(e.target.value) || 0)}
                    className="h-8 text-sm"
                    min="0"
                    data-testid="discount-amount"
                  />
                </div>
                <div className="flex justify-between font-bold text-lg text-slate-900 pt-2 border-t border-slate-200">
                  <span>Total</span>
                  <span>${total.toFixed(2)}</span>
                </div>
              </div>

              <div className="mb-4">
                <Label className="text-sm font-medium text-slate-700 mb-2 block">Payment Type</Label>
                <Select value={paymentType} onValueChange={setPaymentType}>
                  <SelectTrigger data-testid="payment-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="manual">Paid (Cash/Check/Other)</SelectItem>
                    <SelectItem value="invoice">Invoice (Net Terms)</SelectItem>
                    <SelectItem value="stripe">Stripe (Send Payment Link)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Button
                type="submit"
                className="w-full bg-sky-600 hover:bg-sky-700 text-white py-6"
                disabled={submitting || items.length === 0}
                data-testid="submit-manual-order-btn"
              >
                {submitting ? "Creating..." : "Create Order"}
              </Button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}
